// ============================================================================
// Analysis model — one derivation pass, shared by every panel.
// ----------------------------------------------------------------------------
// The whole Analysis board reads from the `projects` prop App.jsx already holds
// (GET /api/projects?summary=true, ORDER BY updated_at DESC LIMIT 200). Zero new
// read endpoints.
//
// Every value here comes from a helper that the Run Sheet also uses, imported
// rather than reimplemented. That is the point: if Analysis says a job is worth
// $12,991 and in the "awaiting answer" lane, the Run Sheet must agree, and the
// only way to guarantee that is to run the same function. Two copies of a
// pricing rule drift, and the drift shows up as the two pages disagreeing about
// money in front of the operator.
// ============================================================================

import { quoteValueExGst, laneOf, type LaneKey } from '../../pages/DashboardPage';
import { getQuoteOutcome } from '../quote-outcome';
import { classifyProjectCategories, matchesRunSheetSearch } from '../history-search';

export type { LaneKey };
export type QuoteOutcome = ReturnType<typeof getQuoteOutcome>;
export type Period = 'last12m' | 'fyToDate' | 'all';

/** One set of period words, so the bar and the drawer cannot describe it differently. */
export const PERIOD_LABELS: Record<Period, string> = {
    last12m: 'Last 12 months',
    fyToDate: 'This financial year',
    all: 'All time',
};

const DAY = 86400000;

/** The API's hard page size. A full board is a truncated board, and says so. */
export const SUMMARY_LIMIT = 200;

export interface DerivedRow {
    id: string;
    title: string;
    address: string;
    client: string;
    stage: number | null;
    quoteNumber: string;
    /** Ex GST. Computed ONCE, on the summary object, and carried. See below. */
    value: number;
    lane: LaneKey;
    outcome: QuoteOutcome;
    /** created_at only. NEVER updated_at — see the note on createdMs below. */
    createdMs: number;
    /** Whole days since createdMs, at the `now` passed to deriveRows. */
    days: number;
    categories: string[];
    /** The original summary object, for the evidence drawer and drill handoff. */
    raw: any;
}

export interface WindowStamp {
    n: number;
    /** True when the API page filled, so the board is a truncated view. */
    capped: boolean;
    earliestCreated: number | null;
    latestCreated: number | null;
    totalValue: number;
    periodLabel: string;
}

const AU_DATE = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

function toMs(v: unknown): number {
    if (!v) return 0;
    const ms = new Date(v as string).getTime();
    return Number.isFinite(ms) ? ms : 0;
}

/**
 * Derive every row the board needs in a single pass.
 *
 * `quoteValueExGst` is called exactly once per row, HERE, on the summary object,
 * and the result is carried on `DerivedRow.value`. It must never be recomputed
 * downstream from a full project row: summaries carry no `quoteData`, so the
 * helper resolves the projected `scope`, whereas a hydrated row resolves
 * `quoteData.scope` and lands on a different number. Recomputing per panel would
 * make two panels over the same jobs disagree. Pinned by test.
 *
 * `createdMs` is `created_at` and nothing else. `updated_at` is a Dexie
 * cache-write timestamp — verified against live Customer Zero data 2026-07-27,
 * all 109 cached projects carried an `updatedAt` inside one 30-second window —
 * so any ageing built on it reads "today" for every row and destroys the signal.
 */
export function deriveRows(projects: any[], now: number = Date.now()): DerivedRow[] {
    if (!Array.isArray(projects)) return [];
    // Materialised HISTORY rows (project_type='history') are archive exhibits
    // imported from the quote corpus — they carry completed quotes with no
    // outcomes by construction and would poison every Analysis figure
    // (win rate, live/dormant, lane counts). Excluded at the source.
    return projects.filter((p: any) => (p?.projectType || p?.project_type) !== 'history').map((p: any) => {
        const createdMs = toMs(p?.createdAt || p?.created_at);
        const fd = p?.formData || p?.form_data || {};
        return {
            id: String(p?.id ?? ''),
            title: String(p?.title || ''),
            address: String(p?.address || ''),
            client: String(fd.clientCompany || fd.clientName || ''),
            stage: Number.isFinite(Number(p?.currentStage ?? p?.current_stage))
                ? Number(p?.currentStage ?? p?.current_stage)
                : null,
            quoteNumber: String(p?.quoteNumber || p?.quote_number || ''),
            value: quoteValueExGst(p),
            lane: laneOf(p),
            outcome: getQuoteOutcome(p),
            createdMs,
            days: createdMs ? Math.max(0, Math.floor((now - createdMs) / DAY)) : 0,
            categories: classifyProjectCategories(p),
            raw: p,
        };
    });
}

/** The honest description of what the board is looking at. */
export function buildWindowStamp(rows: DerivedRow[]): WindowStamp {
    const dated = rows.map(r => r.createdMs).filter(ms => ms > 0);
    const earliest = dated.length ? Math.min(...dated) : null;
    const latest = dated.length ? Math.max(...dated) : null;
    return {
        n: rows.length,
        capped: rows.length >= SUMMARY_LIMIT,
        earliestCreated: earliest,
        latestCreated: latest,
        totalValue: rows.reduce((s, r) => s + r.value, 0),
        periodLabel: earliest && latest
            ? `${AU_DATE.format(new Date(earliest))} to ${AU_DATE.format(new Date(latest))}`
            : 'no dated jobs',
    };
}

/** Start of the Australian financial year (1 July) containing `now`. */
export function fyStart(now: number): number {
    const d = new Date(now);
    const year = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
    return new Date(year, 6, 1).getTime();
}

/**
 * Narrow rows by period and free text.
 *
 * Free text runs through `matchesRunSheetSearch`, the SAME matcher the Run Sheet
 * uses, so a term that finds eight jobs there finds the same eight here. A second
 * matcher would be a second definition of what "balcony" means.
 */
export function applyScope(
    rows: DerivedRow[],
    period: Period,
    query: string,
    now: number = Date.now(),
): DerivedRow[] {
    let out = rows;
    if (period === 'last12m') {
        const cut = now - 365 * DAY;
        out = out.filter(r => r.createdMs >= cut);
    } else if (period === 'fyToDate') {
        const cut = fyStart(now);
        out = out.filter(r => r.createdMs >= cut);
    }
    const q = (query || '').trim();
    if (q) out = out.filter(r => matchesRunSheetSearch(r.raw, q));
    return out;
}

/** Money, ex GST, whole dollars, AU grouping. One formatter for the whole board. */
export function money(n: number): string {
    return '$' + Math.round(n || 0).toLocaleString('en-AU');
}

/**
 * The census phrase every panel's stamp opens with, written once.
 *
 * It lives HERE and not in registry.ts on purpose. registry.ts value-imports all
 * six panels to build ANALYSIS_PANELS, so a panel importing a value back out of
 * registry closes a cycle: entering through the registry works, but entering
 * through a panel (a per-panel unit test, or Vite chunking a panel first) throws
 * "Cannot access before initialization". model.ts is a leaf that imports no
 * panel, so it is safe for every panel to depend on.
 */
export function censusPhrase(n: number, capped: boolean, noun: string): string {
    return `n=${n}${capped ? ' (capped)' : ''}${noun ? ' ' + noun : ''}`;
}

/** Compact money for headline figures: $1.42M / $312k / $940. */
export function moneyCompact(n: number): string {
    const v = Math.round(n || 0);
    if (Math.abs(v) >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2).replace(/\.?0+$/, '') + 'M';
    if (Math.abs(v) >= 1_000) return '$' + Math.round(v / 1000) + 'k';
    return '$' + v.toLocaleString('en-AU');
}
